import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "../store/store";
import { setSectionDetails, clearSectionDetails } from "../store/planTourSlice";
import { addOfflineSubmissionByCategory } from "../store/stateSlice";
import { saveSectionData } from "../Services/saveSectionData";
import { getAccessToken } from "../Services/getAccessToken";
import { 
  getInitialCycleData, 
  saveInitialCycleData, 
  validateInitialCycleData 
} from "../utils/initialCycleData";
// @ts-ignore
import moment from "moment";

type ProductChecklistSubmission = {
  defects: string[]; okays: string[]; defectCategories: { [item: string]: string };
  evaluationTypes: { [item: string]: string }; defectRemarks: { [item: string]: string };
  okayEvaluationTypes: { [item: string]: string }; missedEvaluationTypes: { [item: string]: string };
  productName?: string; batchNo?: string;
};

type CycleResult = {
  started: boolean; completed: boolean;
  defects: string[]; okays: string[];
  defectCategories: { [item: string]: string }; evaluationTypes: { [item: string]: string };
  defectRemarks: { [item: string]: string }; okayEvaluationTypes: { [item: string]: string };
  missedEvaluationTypes: { [item: string]: string };
  submissions?: ProductChecklistSubmission[];
};

type CycleStatusMap = {
  [cycleNo: number]: CycleResult;
};

type SelectionItem = {
  status: "Okay" | "Not Okay" | null;
  category?: string;
  defect?: string;
  majorDefect?: string;
};

type SelectedMap = {
  [cycleNo: number]: Record<string, SelectionItem>;
};

const productItems: { [key: number]: string[] } = {
  1: ["PP 1", "PP 2", "PP 3", "PP 4","PP 5","PP 6","PP 7","PP 8"],
  2: ["PP 1", "PP 2", "PP 3", "PP 4","PP 5","PP 6","PP 7","PP 8"],
  3: ["PP 1", "PP 2", "PP 3", "PP 4","PP 5","PP 6","PP 7","PP 8"],
  4: ["PP 1", "PP 2", "PP 3", "PP 4","PP 5","PP 6","PP 7","PP 8"],
  5: ["PP 1", "PP 2", "PP 3", "PP 4","PP 5","PP 6","PP 7","PP 8"],
  6: ["PP 1", "PP 2", "PP 3", "PP 4","PP 5","PP 6","PP 7","PP 8"],
  7: ["PP 1", "PP 2", "PP 3", "PP 4","PP 5","PP 6","PP 7","PP 8"],
  8: ["PP 1", "PP 2", "PP 3", "PP 4","PP 5","PP 6","PP 7","PP 8"],
};
const allProductItemNames = ["PP 1", "PP 2", "PP 3", "PP 4", "PP 5", "PP 6", "PP 7", "PP 8"];
function getProductKeyForRecord(item: any): string {
  const pn = (item.cr3ea_productname || '').toString().trim();
  const bn = (item.cr3ea_batchno || '').toString().trim();
  if (allProductItemNames.includes(pn)) return `_${bn}`;
  return `${pn || 'Product'}_${bn}`;
}

const totalCycles = 8;

interface ProductEvaluationProps {
  onCycleComplete: () => void;
  /** When set (e.g. "cr3ea_prod_pqi_bakeries"), save to this table instead of default PQI fronts table */
  sectionDataTableName?: string;
  /** When provided with sectionDataTableName=cr3ea_prod_pqi_bakeries, use Backing PQI store/service */
  backingPQISave?: {
    addOffline: (payload: { cycleNo: number; records: any[] }) => void;
    saveOnline: (records: any[]) => Promise<void>;
  };
}

const ProductEvaluation: React.FC<ProductEvaluationProps> = ({
  onCycleComplete,
  sectionDataTableName,
  backingPQISave
}) => {
  const dispatch = useDispatch();
  const sectionDetails = useSelector((state: RootState) => state.planTour.sectionDetails);
  const user = useSelector((state: RootState) => state.user.user);
  const selectedShift = useSelector((state: RootState) => state.planTour.selectedCycle);
  const plantTourId = useSelector((state: RootState) => state.planTour.plantTourId);
  const isOfflineStarted = useSelector((state: RootState) => state.appState.isOfflineStarted);
  const offlineSubmissions = useSelector((state: RootState) => state.appState.offlineSubmissions);
  const offlineSubmissionsByCategory = useSelector((state: RootState) => state.appState.offlineSubmissionsByCategory);
  const reduxData = useSelector((state: RootState) => state.planTour.cycleData);
  const reduxCycleData = reduxData.filter((item: any) => 
    item.cr3ea_category === 'Product'
  );
  console.log(reduxCycleData,'reduxCycleData');
  
  const hasProcessedOfflineRef = useRef(false);
  const [cycleStatus, setCycleStatus] = useState<CycleStatusMap>({});
  const [selected, setSelected] = useState<SelectedMap>({});
  const [activeCycle, setActiveCycle] = useState<number>(1);
  const [formFields, setFormFields] = useState<{ [cycleNo: number]: any }>({});
  const [expandedCompletedCycle, setExpandedCompletedCycle] = useState<number | null>(null);
  const [productFormExpandedByCycle, setProductFormExpandedByCycle] = useState<{ [cycleNo: number]: boolean }>({});

  const handleExpand = (cycleNo: number | null) => {
    setExpandedCompletedCycle(cycleNo);
    localStorage.setItem('expandedProductCycle', cycleNo !== null ? String(cycleNo) : '');
  };

  const handleFormFieldChange = (cycleNo: number, field: string, value: string) => {
    setFormFields((prev) => {
      const updatedForCycle = { ...(prev[cycleNo] || {}), [field]: value };
      const next = { ...prev, [cycleNo]: updatedForCycle } as { [cycle: number]: any };
      const details = {
        product: updatedForCycle.product || '',
        partyName: updatedForCycle.partyName || '',
        batchNo: updatedForCycle.batchNo || '',
        lineNo: updatedForCycle.lineNo || '',
        expiry: updatedForCycle.expiry || '',
        packaged: updatedForCycle.packaged || '',
        executiveName: updatedForCycle.executiveName || '',
        shift: selectedShift || '',
        evaluationType: '',
        criteria: '',
        cycleNum: cycleNo,
      };
      dispatch(setSectionDetails({ cycleNo, details }));
      
      // If this is cycle 1 and we have all required fields, save as initial cycle data
      if (cycleNo === 1 && validateInitialCycleData(updatedForCycle)) {
        console.log('ProductEvaluation: Saving initial cycle data for auto-fill:', updatedForCycle);
        saveInitialCycleData(updatedForCycle);
      }
      
      return next;
    });
  };

  // Auto-fill start fields from localStorage initial cycle data
  useEffect(() => {
    const initialData = getInitialCycleData();
    if (!initialData) return;
    
    console.log('ProductEvaluation: Auto-filling from initial cycle data:', initialData);
    
    setFormFields((prev) => {
      const next = { ...prev } as { [cycle: number]: any };
      for (let i = 1; i <= totalCycles; i++) {
        const current = next[i] || {};
        // Only auto-fill if the field is empty (don't overwrite existing data)
        next[i] = {
          ...current,
          product: current.product || initialData.product || '',
          partyName: current.partyName || initialData.partyName || '',
          batchNo: current.batchNo || initialData.batchNo || '',
          lineNo: current.lineNo || initialData.lineNo || '',
          packaged: current.packaged || initialData.packaged || '',
          expiry: current.expiry || initialData.expiry || '',
          executiveName: current.executiveName || initialData.executiveName || '',
        };
      }
      return next;
    });
  }, []); // Run only once on component mount

  const processProductData = useCallback((cycleData: any[]) => {
    console.log('ProductEvaluation: Processing Product data', { cycleDataLength: cycleData?.length });
    if (!cycleData || cycleData.length === 0) return;

    // Filter data for Product category
    const productData = cycleData.filter((item: any) => 
      item.cr3ea_category === 'Product'
    );

    console.log('ProductEvaluation: Filtered Product data', { productDataLength: productData.length });

    if (productData.length === 0) return;

    // Initialize new state objects
    const newCycleStatus: CycleStatusMap = {};
    const newSelected: SelectedMap = {};

    const cycleSubmissions: { [cycleNo: number]: ProductChecklistSubmission[] } = {};
    const itemsPerProduct = productItems[1]?.length || 8;

    for (let cycleNo = 1; cycleNo <= totalCycles; cycleNo++) {
      const cycleData = productData.filter((item: any) => {
        const cycleMatch = item.cr3ea_cycle?.match(/Cycle-(\d+)/);
        return cycleMatch && parseInt(cycleMatch[1]) === cycleNo;
      });

      if (cycleData.length > 0) {
        const defects: string[] = [];
        const okays: string[] = [];
        const defectCategories: { [key: string]: string } = {};
        const evaluationTypes: { [key: string]: string } = {};
        const defectRemarks: { [key: string]: string } = {};
        const okayEvaluationTypes: { [key: string]: string } = {};
        const missedEvaluationTypes: { [key: string]: string } = {};
        if (!newSelected[cycleNo]) newSelected[cycleNo] = {};
        if (!cycleSubmissions[cycleNo]) cycleSubmissions[cycleNo] = [];

        const productKeys = [...new Set(cycleData.map((r: any) => getProductKeyForRecord(r)))];
        const useProductKeyGrouping = productKeys.length > 1 || (productKeys.length === 1 && cycleData.length <= itemsPerProduct);

        const processRecordIntoSub = (item: any, productSub: ProductChecklistSubmission) => {
          const evaluationType = item.cr3ea_evaluationtype || item.cr3ea_defect || 'Unknown';
          if (item.cr3ea_criteria === 'Okay') {
            okays.push(evaluationType);
            okayEvaluationTypes[evaluationType] = evaluationType;
            productSub.okays.push(evaluationType);
            productSub.okayEvaluationTypes[evaluationType] = evaluationType;
            newSelected[cycleNo][evaluationType] = { status: 'Okay', category: undefined, defect: undefined, majorDefect: undefined };
          } else if (item.cr3ea_criteria === 'Not Okay') {
            if (item.cr3ea_defectcategory === 'Missed') {
              missedEvaluationTypes[evaluationType] = evaluationType;
              productSub.missedEvaluationTypes[evaluationType] = evaluationType;
            } else {
              defects.push(evaluationType);
              defectCategories[evaluationType] = item.cr3ea_defectcategory || 'Category B';
              evaluationTypes[evaluationType] = evaluationType;
              defectRemarks[evaluationType] = item.cr3ea_defectremarks || '';
              productSub.defects.push(evaluationType);
              productSub.defectCategories[evaluationType] = item.cr3ea_defectcategory || 'Category B';
              productSub.evaluationTypes[evaluationType] = evaluationType;
              productSub.defectRemarks[evaluationType] = item.cr3ea_defectremarks || '';
              newSelected[cycleNo][evaluationType] = { status: 'Not Okay', category: item.cr3ea_defectcategory || 'Category B', defect: item.cr3ea_defect || '', majorDefect: item.cr3ea_defectremarks || '' };
            }
          } else if (!item.cr3ea_criteria || item.cr3ea_criteria === null) {
            missedEvaluationTypes[evaluationType] = evaluationType;
            productSub.missedEvaluationTypes[evaluationType] = evaluationType;
          }
        };

        if (useProductKeyGrouping) {
          cycleData.forEach((item: any) => {
            const productKey = getProductKeyForRecord(item);
            let productSub = cycleSubmissions[cycleNo].find((s: ProductChecklistSubmission) => getProductKeyForRecord({ cr3ea_productname: s.productName, cr3ea_batchno: s.batchNo }) === productKey);
            if (!productSub) {
              const effectiveProductName = allProductItemNames.includes((item.cr3ea_productname || '').toString().trim()) ? '' : (item.cr3ea_productname || '');
              productSub = { defects: [], okays: [], defectCategories: {}, evaluationTypes: {}, defectRemarks: {}, okayEvaluationTypes: {}, missedEvaluationTypes: {}, productName: effectiveProductName, batchNo: item.cr3ea_batchno || '' };
              cycleSubmissions[cycleNo].push(productSub);
            }
            processRecordIntoSub(item, productSub);
          });
        } else {
          cycleData.forEach((item: any) => {
            const ppItem = item.cr3ea_evaluationtype || item.cr3ea_defect || 'Unknown';
            const hasItem = (s: ProductChecklistSubmission) => s.okays.includes(ppItem) || s.defects.includes(ppItem) || (s.missedEvaluationTypes && ppItem in s.missedEvaluationTypes);
            let productSub = cycleSubmissions[cycleNo].find((s: ProductChecklistSubmission) => !hasItem(s));
            if (!productSub) {
              const effectiveProductName = allProductItemNames.includes((item.cr3ea_productname || '').toString().trim()) ? '' : (item.cr3ea_productname || '');
              productSub = { defects: [], okays: [], defectCategories: {}, evaluationTypes: {}, defectRemarks: {}, okayEvaluationTypes: {}, missedEvaluationTypes: {}, productName: effectiveProductName, batchNo: item.cr3ea_batchno || '' };
              cycleSubmissions[cycleNo].push(productSub);
            }
            processRecordIntoSub(item, productSub);
          });
        }

        const subs = cycleSubmissions[cycleNo] || [];
        const existingStatus = cycleStatus[cycleNo];
        const existingSubCount = existingStatus?.submissions?.length ?? 0;
        if (existingSubCount > 0 && existingSubCount !== subs.length) {
          newCycleStatus[cycleNo] = existingStatus;
        } else {
          newCycleStatus[cycleNo] = {
            started: true,
            completed: true,
            defects,
            okays,
            defectCategories,
            evaluationTypes,
            defectRemarks,
            okayEvaluationTypes,
            missedEvaluationTypes,
            submissions: subs.length > 0 ? subs : undefined
          };
        }
      }
    }

    // Find the next available cycle
    let nextAvailableCycle = 1;
    while (nextAvailableCycle <= totalCycles && newCycleStatus[nextAvailableCycle]?.completed) {
      nextAvailableCycle++;
    }

    console.log('ProductEvaluation: Processed cycle details:', newCycleStatus);
    console.log('ProductEvaluation: Updated selected state:', newSelected);
    console.log('ProductEvaluation: Next available cycle:', nextAvailableCycle);

    // Only update state if there are actual changes
    const currentStatusHash = JSON.stringify(cycleStatus);
    const newStatusHash = JSON.stringify(newCycleStatus);
    
    if (currentStatusHash !== newStatusHash || nextAvailableCycle !== activeCycle) {
      console.log(`ProductEvaluation: Updating activeCycle from ${activeCycle} to ${nextAvailableCycle}`);
      setCycleStatus(newCycleStatus);
      setSelected(newSelected);
      setActiveCycle(nextAvailableCycle);
      console.log('ProductEvaluation: Updated cycle status:', newCycleStatus);
      console.log('ProductEvaluation: Updated selected state:', newSelected);
      console.log('ProductEvaluation: Next available cycle:', nextAvailableCycle);
    } else {
      console.log('ProductEvaluation: No changes detected, skipping state updates');
    }
  }, [reduxCycleData, cycleStatus]);

  // Process offline data for Product evaluation
  const processOfflineData = () => {
    if (!isOfflineStarted) {
      return;
    }

    console.log("ProductEvaluation: Offline mode: Processing offline submissions for cycle details");
    console.log("ProductEvaluation: Offline submissions length:", offlineSubmissions.length);
    console.log("ProductEvaluation: Redux cycle data length:", reduxCycleData?.length || 0);

    // Initialize cycle status and selected state
    const newCycleStatus: CycleStatusMap = {};
    const newSelected: SelectedMap = {};

    // First, process Redux data if available (for existing completed cycles)
    if (reduxCycleData && reduxCycleData.length > 0) {
      console.log("ProductEvaluation: Offline mode: Processing Redux data for existing completed cycles");
      
      // Filter data for Product category
      const productData = reduxCycleData.filter((item: any) => 
        item.cr3ea_category === 'Product'
      );

      if (productData.length > 0) {
        const cycleSubmissionsRedux: { [cycleNo: number]: ProductChecklistSubmission[] } = {};
        for (let cycleNo = 1; cycleNo <= totalCycles; cycleNo++) {
          const cycleData = productData.filter((item: any) => {
            const cycleMatch = item.cr3ea_cycle?.match(/Cycle-(\d+)/);
            return cycleMatch && parseInt(cycleMatch[1]) === cycleNo;
          });

          if (cycleData.length > 0) {
            const defects: string[] = [];
            const okays: string[] = [];
            const defectCategories: { [key: string]: string } = {};
            const evaluationTypes: { [key: string]: string } = {};
            const defectRemarks: { [key: string]: string } = {};
            const okayEvaluationTypes: { [key: string]: string } = {};
            const missedEvaluationTypes: { [key: string]: string } = {};
            if (!newSelected[cycleNo]) newSelected[cycleNo] = {};
            if (!cycleSubmissionsRedux[cycleNo]) cycleSubmissionsRedux[cycleNo] = [];

            cycleData.forEach((item: any) => {
              const evaluationType = item.cr3ea_evaluationtype || item.cr3ea_defect || 'Unknown';
              const productKey = getProductKeyForRecord(item);
              let productSub = cycleSubmissionsRedux[cycleNo].find((s: ProductChecklistSubmission) => getProductKeyForRecord({ cr3ea_productname: s.productName, cr3ea_batchno: s.batchNo }) === productKey);
              if (!productSub) {
                const effectiveProductName = allProductItemNames.includes((item.cr3ea_productname || '').toString().trim()) ? '' : (item.cr3ea_productname || '');
                productSub = { defects: [], okays: [], defectCategories: {}, evaluationTypes: {}, defectRemarks: {}, okayEvaluationTypes: {}, missedEvaluationTypes: {}, productName: effectiveProductName, batchNo: item.cr3ea_batchno || '' };
                cycleSubmissionsRedux[cycleNo].push(productSub);
              }
              if (item.cr3ea_criteria === 'Okay') {
                okays.push(evaluationType);
                okayEvaluationTypes[evaluationType] = evaluationType;
                productSub.okays.push(evaluationType);
                productSub.okayEvaluationTypes[evaluationType] = evaluationType;
                newSelected[cycleNo][evaluationType] = { status: 'Okay', category: undefined, defect: undefined, majorDefect: undefined };
              } else if (item.cr3ea_criteria === 'Not Okay') {
                if (item.cr3ea_defectcategory === 'Missed') {
                  missedEvaluationTypes[evaluationType] = evaluationType;
                  productSub.missedEvaluationTypes[evaluationType] = evaluationType;
                } else {
                  defects.push(evaluationType);
                  defectCategories[evaluationType] = item.cr3ea_defectcategory || 'Category B';
                  evaluationTypes[evaluationType] = evaluationType;
                  defectRemarks[evaluationType] = item.cr3ea_defectremarks || '';
                  productSub.defects.push(evaluationType);
                  productSub.defectCategories[evaluationType] = item.cr3ea_defectcategory || 'Category B';
                  productSub.evaluationTypes[evaluationType] = evaluationType;
                  productSub.defectRemarks[evaluationType] = item.cr3ea_defectremarks || '';
                  newSelected[cycleNo][evaluationType] = { status: 'Not Okay', category: item.cr3ea_defectcategory || 'Category B', defect: item.cr3ea_defect || '', majorDefect: item.cr3ea_defectremarks || '' };
                }
              } else if (!item.cr3ea_criteria || item.cr3ea_criteria === null) {
                missedEvaluationTypes[evaluationType] = evaluationType;
                productSub.missedEvaluationTypes[evaluationType] = evaluationType;
              }
            });

            const subs = cycleSubmissionsRedux[cycleNo] || [];
            newCycleStatus[cycleNo] = {
              started: true,
              completed: true,
              defects,
              okays,
              defectCategories,
              evaluationTypes,
              defectRemarks,
              okayEvaluationTypes,
              missedEvaluationTypes,
              submissions: subs.length > 0 ? subs : undefined
            };
          }
        }
      }
    }

    // Then, process offline submissions (this will override/add to Redux data)
    const productOfflineSubmissions = offlineSubmissionsByCategory['Product'] || [];
    if (productOfflineSubmissions.length > 0) {
      console.log("ProductEvaluation: Offline mode: Processing Product offline submissions:", productOfflineSubmissions.length);
      
      const byCycle: { [cycleNo: number]: ProductChecklistSubmission[] } = {};
      productOfflineSubmissions.forEach(submission => {
        const cycleNo = submission.cycleNo;
        const productRecords = submission.records;
        const defects: string[] = [];
        const okays: string[] = [];
        const defectCategories: { [key: string]: string } = {};
        const evaluationTypes: { [key: string]: string } = {};
        const defectRemarks: { [key: string]: string } = {};
        const okayEvaluationTypes: { [key: string]: string } = {};
        const missedEvaluationTypes: { [key: string]: string } = {};
        productRecords.forEach((record: any) => {
          if (record.cr3ea_criteria === 'Okay') {
            okays.push(record.cr3ea_evaluationtype);
            okayEvaluationTypes[record.cr3ea_evaluationtype] = record.cr3ea_evaluationtype;
          } else if (record.cr3ea_criteria === 'Not Okay') {
            if (record.cr3ea_defectcategory === 'Missed') missedEvaluationTypes[record.cr3ea_defect] = record.cr3ea_defect;
            else {
              defects.push(record.cr3ea_evaluationtype);
              defectCategories[record.cr3ea_evaluationtype] = record.cr3ea_defectcategory || 'Category B';
              evaluationTypes[record.cr3ea_evaluationtype] = record.cr3ea_evaluationtype;
              defectRemarks[record.cr3ea_evaluationtype] = record.cr3ea_defectremarks || '';
            }
          }
        });
        const sub: ProductChecklistSubmission = { defects, okays, defectCategories, evaluationTypes, defectRemarks, okayEvaluationTypes, missedEvaluationTypes, productName: productRecords[0]?.cr3ea_productname, batchNo: productRecords[0]?.cr3ea_batchno };
        if (!byCycle[cycleNo]) byCycle[cycleNo] = [];
        byCycle[cycleNo].push(sub);
      });
      Object.entries(byCycle).forEach(([cycleNoStr, submissions]) => {
        const cycleNo = parseInt(cycleNoStr);
        newSelected[cycleNo] = {};
        newCycleStatus[cycleNo] = {
          started: true, completed: true,
          defects: submissions.flatMap(s => s.defects), okays: submissions.flatMap(s => s.okays),
          defectCategories: Object.assign({}, ...submissions.map(s => s.defectCategories)),
          evaluationTypes: Object.assign({}, ...submissions.map(s => s.evaluationTypes)),
          defectRemarks: Object.assign({}, ...submissions.map(s => s.defectRemarks)),
          okayEvaluationTypes: Object.assign({}, ...submissions.map(s => s.okayEvaluationTypes)),
          missedEvaluationTypes: Object.assign({}, ...submissions.map(s => s.missedEvaluationTypes)),
          submissions
        };
      });
     }
    
    // Find the next available cycle (first non-completed cycle)
    let nextAvailableCycle = 1;
    while (nextAvailableCycle <= totalCycles && newCycleStatus[nextAvailableCycle]?.completed) {
      nextAvailableCycle++;
    }
    
    console.log('ProductEvaluation: Offline mode - calculated nextAvailableCycle:', nextAvailableCycle);
    console.log('ProductEvaluation: Offline mode - completed cycles:', Object.entries(newCycleStatus).filter(([_, status]) => status.completed).map(([cycleNo, _]) => cycleNo));
    console.log('ProductEvaluation: Offline mode - newCycleStatus:', newCycleStatus);
    console.log('ProductEvaluation: Offline mode - newSelected:', newSelected);
    
    // Update cycle status and selected state
    setCycleStatus(newCycleStatus);
    setSelected(newSelected);
    setActiveCycle(nextAvailableCycle);
    
    console.log('ProductEvaluation: Offline mode - Updated cycle status to:', newCycleStatus);
    console.log('ProductEvaluation: Offline mode - Updated active cycle to:', nextAvailableCycle);
    
    console.log('ProductEvaluation: Offline data processed for summary display');
    console.log('ProductEvaluation: Processed cycle status:', newCycleStatus);
    console.log('ProductEvaluation: Updated selected state:', newSelected);
    console.log('ProductEvaluation: Next available cycle:', nextAvailableCycle);
    console.log('ProductEvaluation: Active cycle updated to:', nextAvailableCycle);
  };

  // Effect to process Redux data when it changes
  useEffect(() => {
    console.log('ProductEvaluation: useEffect triggered with reduxCycleData length:', reduxCycleData?.length, 'isOfflineStarted:', isOfflineStarted);
    
    if (reduxCycleData && reduxCycleData.length > 0) {
      const currentDataHash = JSON.stringify(reduxCycleData);
      const lastProcessedHash = localStorage.getItem('lastProcessedProductDataHash');
      
      if (currentDataHash !== lastProcessedHash) {
        console.log('ProductEvaluation: Processing new Product data');
        processProductData(reduxCycleData);
        localStorage.setItem('lastProcessedProductDataHash', currentDataHash);
      } else {
        console.log('ProductEvaluation: Skipping Product data processing - no changes detected');
      }
    }
  }, [reduxCycleData, processProductData]);

  useEffect(() => {
    if (isOfflineStarted && !hasProcessedOfflineRef.current) {
      hasProcessedOfflineRef.current = true;
      processOfflineData();
    }
    if (!isOfflineStarted) hasProcessedOfflineRef.current = false;
  }, [isOfflineStarted]);

  // Cleanup effect to clear localStorage when plantTourId changes or component unmounts
  useEffect(() => {
    return () => {
      localStorage.removeItem('lastProcessedProductDataHash');
    };
  }, [plantTourId]);

  // Clear localStorage when plantTourId changes to ensure fresh data processing
  useEffect(() => {
    localStorage.removeItem('lastProcessedProductDataHash');
    console.log('ProductEvaluation: Cleared localStorage data hash due to plantTourId change');
  }, [plantTourId]);

  // Effect to restore expanded cycle state on mount
  useEffect(() => {
    const savedExpandedCycle = localStorage.getItem('expandedProductCycle');
    if (savedExpandedCycle) {
      setExpandedCompletedCycle(parseInt(savedExpandedCycle));
    }
  }, []);

  // Debug effect to log component state on mount
  useEffect(() => {
    console.log('ProductEvaluation: Component mounted - checking persisted data');
    console.log('ProductEvaluation: isOfflineStarted:', isOfflineStarted);
    console.log('ProductEvaluation: offlineSubmissions length:', offlineSubmissions?.length);
    console.log('ProductEvaluation: offlineSubmissions:', offlineSubmissions);
    console.log('ProductEvaluation: reduxCycleData length:', reduxCycleData?.length);
    console.log('ProductEvaluation: reduxCycleData:', reduxCycleData);
    console.log('ProductEvaluation: Current cycleStatus:', cycleStatus);
    console.log('ProductEvaluation: Current activeCycle:', activeCycle);
    console.log('ProductEvaluation: Offline submissions:', offlineSubmissions);
  }, []);

  // Debug effect to monitor cycle status changes
  useEffect(() => {
    console.log('ProductEvaluation: Cycle status changed:', cycleStatus);
    console.log('ProductEvaluation: Active cycle changed to:', activeCycle);
    console.log('ProductEvaluation: Offline submissions changed:', offlineSubmissions);
  }, [cycleStatus, activeCycle, offlineSubmissions]);

  const handleStart = (cycleNo: number) => {
    const items = productItems[cycleNo] || [];
    const initialState: Record<string, SelectionItem> = {};
    items.forEach((item) => {
      initialState[item] = { status: null };
    });

    setCycleStatus((prev: CycleStatusMap) => ({
      ...prev,
      [cycleNo]: { 
        ...prev[cycleNo], 
        started: true,
        completed: false,
        defects: [],
        okays: [],
        defectCategories: {},
        evaluationTypes: {},
        defectRemarks: {},
        okayEvaluationTypes: {},
        missedEvaluationTypes: {}
      },
    }));

    setSelected((prev: SelectedMap) => ({
      ...prev,
      [cycleNo]: initialState,
    }));

    const details = {
      product: formFields[cycleNo]?.product || '',
      partyName: formFields[cycleNo]?.partyName || '',
      batchNo: formFields[cycleNo]?.batchNo || '',
      lineNo: formFields[cycleNo]?.lineNo || '',
      expiry: formFields[cycleNo]?.expiry || '',
      packaged: formFields[cycleNo]?.packaged || '',
      executiveName: formFields[cycleNo]?.executiveName || '',
      shift: selectedShift || '',
      evaluationType: '',
      criteria: '',
      cycleNum: cycleNo,
    };
    dispatch(setSectionDetails({ cycleNo, details }));
  };

  const handleSelect = (
    cycleNo: number,
    item: string,
    value: "Okay" | "Not Okay"
  ) => {
    setSelected((prev: SelectedMap) => ({
      ...prev,
      [cycleNo]: {
        ...prev[cycleNo],
        [item]: {
          ...prev[cycleNo][item],
          status: value,
        },
      },
    }));
  };

  const updateField = (
    cycleNo: number,
    item: string,
    field: keyof SelectionItem,
    value: string
  ) => {
    setSelected((prev: SelectedMap) => ({
      ...prev,
      [cycleNo]: {
        ...prev[cycleNo],
        [item]: {
          ...prev[cycleNo][item],
          [field]: value,
        },
      },
    }));
  };

  const handleSave = async (cycleNo: number) => {
    const defects: string[] = [];
    const okays: string[] = [];
    const currentSelections = selected[cycleNo];
    Object.entries(currentSelections || {}).forEach(([key, val]) => {
      if (val.status === "Okay") okays.push(key);
      else if (val.status === "Not Okay") defects.push(key);
    });

    const details = sectionDetails[cycleNo] || {};
    const records = Object.entries(currentSelections || {}).map(([item, val]) => {
      const base = {
        cr3ea_evaluationtype: item,
        cr3ea_criteria: val.status,
        cr3ea_cycle: `Cycle-${cycleNo}`,
        cr3ea_title: 'QA_' + moment().format('DD/MM/YYYY'),
        cr3ea_expiry: details.expiry || '',
        cr3ea_shift: details.shift || '',
        cr3ea_batchno: details.batchNo || '',
        cr3ea_lineno: details.lineNo || '',
        cr3ea_category: 'Product',
        cr3ea_pkd: details.packaged || '',
        cr3ea_tourstartdate: moment().format('DD/MM/YYYY'),
        cr3ea_productname: details.product || '',
        cr3ea_partyname: details.partyName || '',
        cr3ea_observedby: user?.Name || '',
        cr3ea_qualitytourid: plantTourId || '',
      };

      if (val.status === "Not Okay") {
        return {
          ...base,
          cr3ea_defectcategory: val.category || 'Category B',
          cr3ea_defect: val.defect || '',
          cr3ea_defectremarks: val.majorDefect || '',
        };
      }
      return base;
    });

    const allProductItems = productItems[cycleNo] || [];
    const evaluatedItems = Object.keys(currentSelections || {});
    const missedItems = allProductItems.filter(item => !evaluatedItems.includes(item));
    
    missedItems.forEach(item => {
      records.push({
        cr3ea_evaluationtype: details.evaluationType || '',
        cr3ea_criteria: 'Not Okay',
        cr3ea_cycle: `Cycle-${cycleNo}`,
        cr3ea_title: 'QA_' + moment().format('DD/MM/YYYY'),
        cr3ea_expiry: details.expiry || '',
        cr3ea_shift: details.shift || '',
        cr3ea_batchno: details.batchNo || '',
        cr3ea_lineno: details.lineNo || '',
        cr3ea_category: 'Product',
        cr3ea_pkd: details.packaged || '',
        cr3ea_tourstartdate: moment().format('DD/MM/YYYY'),
        cr3ea_productname: details.product || '',
        cr3ea_partyname: details.partyName || '',
        cr3ea_observedby: user?.Name || '',
        cr3ea_qualitytourid: plantTourId || '',
        cr3ea_defect: item,
        cr3ea_defectcategory: 'Missed',
        cr3ea_defectremarks: 'Missed evaluation',
      });
    });

    const useBackingPQI = sectionDataTableName === 'cr3ea_prod_pqi_bakeries' && backingPQISave;
    if (isOfflineStarted) {
      if (useBackingPQI) {
        backingPQISave!.addOffline({ cycleNo, records });
      } else {
        dispatch(addOfflineSubmissionByCategory({ category: 'Product', submission: { cycleNo, records, timestamp: Date.now(), plantTourId: plantTourId || '', ...(sectionDataTableName ? { sectionDataTableName } : {}) } }));
      }
    } else {
      if (useBackingPQI) {
        await backingPQISave!.saveOnline(records);
      } else {
        const tokenResult = await getAccessToken();
        const accessToken = tokenResult?.token;
        if (!accessToken) { alert('No access token available'); return; }
        await saveSectionData(accessToken, records);
      }
    }

    // Collect defect details for this product checklist
    const defectCategories: { [item: string]: string } = {};
    const evaluationTypes: { [item: string]: string } = {};
    const defectRemarks: { [item: string]: string } = {};
    const okayEvaluationTypes: { [item: string]: string } = {};
    const missedEvaluationTypes: { [item: string]: string } = {};
    
    // Process current selections to collect details
    Object.entries(currentSelections || {}).forEach(([item, val]) => {
      if (val.status === "Okay") {
        okayEvaluationTypes[item] = item;
      } else if (val.status === "Not Okay") {
        defectCategories[item] = val.category || 'Category B';
        evaluationTypes[item] = item;
        defectRemarks[item] = val.majorDefect || '';
      }
    });
    
    missedItems.forEach(item => { missedEvaluationTypes[item] = item; });
    
    const newSubmission: ProductChecklistSubmission = {
      defects, okays, defectCategories, evaluationTypes, defectRemarks,
      okayEvaluationTypes, missedEvaluationTypes,
      productName: details.product || '', batchNo: details.batchNo || ''
    };
    setCycleStatus((prev: CycleStatusMap) => {
      const existing = prev[cycleNo] || { started: false, completed: false, defects: [], okays: [], defectCategories: {}, evaluationTypes: {}, defectRemarks: {}, okayEvaluationTypes: {}, missedEvaluationTypes: {} };
      const submissions = [...(existing.submissions || []), newSubmission];
      return {
        ...prev,
        [cycleNo]: {
          ...existing, started: true, completed: false,
          defects: submissions.flatMap(s => s.defects), okays: submissions.flatMap(s => s.okays),
          defectCategories: Object.assign({}, ...submissions.map(s => s.defectCategories)),
          evaluationTypes: Object.assign({}, ...submissions.map(s => s.evaluationTypes)),
          defectRemarks: Object.assign({}, ...submissions.map(s => s.defectRemarks)),
          okayEvaluationTypes: Object.assign({}, ...submissions.map(s => s.okayEvaluationTypes)),
          missedEvaluationTypes: Object.assign({}, ...submissions.map(s => s.missedEvaluationTypes)),
          submissions
        },
      };
    });
    setSelected((prev: SelectedMap) => ({ ...prev, [cycleNo]: {} }));
    setProductFormExpandedByCycle((prev) => ({ ...prev, [cycleNo]: false }));
    if (!isOfflineStarted) alert(`Product checklist saved. Add another product or click "Complete Cycle & Move to Next".`);
  };

  const handleCompleteCycle = (cycleNo: number) => {
    setCycleStatus((prev: CycleStatusMap) => ({ ...prev, [cycleNo]: { ...prev[cycleNo], completed: true } }));
    let nextAvailableCycle = 1;
    const completedCycles = new Set<number>();
    Object.entries(cycleStatus).forEach(([n, s]) => { if (s.completed) completedCycles.add(parseInt(n)); });
    completedCycles.add(cycleNo);
    while (nextAvailableCycle <= totalCycles && completedCycles.has(nextAvailableCycle)) nextAvailableCycle++;
    setActiveCycle(nextAvailableCycle);
    dispatch(clearSectionDetails(cycleNo));
    if (onCycleComplete) onCycleComplete();
  };

  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto">
      {Array.from({ length: totalCycles }, (_, i) => {
        const cycleNo = i + 1;
        const status = cycleStatus[cycleNo] || { started: false, completed: false, defects: [], okays: [], defectCategories: {}, evaluationTypes: {}, defectRemarks: {}, okayEvaluationTypes: {}, missedEvaluationTypes: {}, submissions: [] };
        const totalMissedCount = (status.submissions || []).length > 0 ? (status.submissions || []).reduce((sum, s) => sum + Object.keys(s.missedEvaluationTypes || {}).length, 0) : Object.keys(status.missedEvaluationTypes || {}).length;
        const items = productItems[cycleNo] || [];
        const shouldShow = status.completed || cycleNo === activeCycle || cycleNo === activeCycle + 1;
        if (!shouldShow) return null;
        const isDisabled = !status.completed && cycleNo !== activeCycle;
        return (
          <div key={cycleNo} className={`light-surface border rounded-md p-4 shadow-sm ${isDisabled ? "bg-gray-100 opacity-60" : "bg-white"}`}>
            <div className="flex justify-between items-center mb-3"><h2 className="text-md font-semibold">Cycle {cycleNo}</h2></div>
            {/* Show completed product(s) summary when 1+ product checklists saved */}
            {!status.completed && cycleNo === activeCycle && status.started && Object.keys(selected[cycleNo] || {}).length === 0 && (status.submissions?.length || 0) >= 1 && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-green-700 font-semibold">✓ {status.submissions?.length || 0} Product checklist(s) completed</span>
                  <button onClick={() => handleCompleteCycle(cycleNo)} className="px-4 py-2 bg-green-600 text-white rounded font-medium">
                    Complete Cycle & Move to Next
                  </button>
                </div>
                <div className="space-y-3">
                  {(status.submissions || []).map((sub, idx) => {
                    const total = sub.defects.length + sub.okays.length + Object.keys(sub.missedEvaluationTypes || {}).length;
                    return (
                      <div key={idx} className="p-3 bg-white border border-gray-200 rounded-lg">
                        <div className="font-medium text-gray-800 mb-2">
                          Product {idx + 1}{sub.productName ? ` — ${sub.productName}` : ''}{sub.batchNo ? ` (Batch: ${sub.batchNo})` : ''}
                        </div>
                        <div className="flex flex-wrap gap-2 text-sm">
                          {sub.defects.length > 0 && <span className="text-red-600 bg-red-100 px-2 py-0.5 rounded-full">{sub.defects.length} Defect(s)</span>}
                          {sub.okays.length > 0 && <span className="text-green-600 bg-green-100 px-2 py-0.5 rounded-full">{sub.okays.length} Okay(s)</span>}
                          {Object.keys(sub.missedEvaluationTypes || {}).length > 0 && <span className="text-yellow-600 bg-yellow-100 px-2 py-0.5 rounded-full">{Object.keys(sub.missedEvaluationTypes || {}).length} Missed</span>}
                          <span className="text-gray-500">{total} items</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!status.completed && cycleNo === activeCycle && (!status.started || (Object.keys(selected[cycleNo] || {}).length === 0 && (status.submissions?.length || 0) >= 1)) && (
              <div className="space-y-6">
                {(!status.started || productFormExpandedByCycle[cycleNo]) ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div><label className="block text-sm font-medium mb-1 text-gray-700">Product</label><input type="text" className="w-full border border-gray-300 rounded px-3 py-2 text-gray-900 bg-white" placeholder="Enter Product" value={formFields[cycleNo]?.product || ''} onChange={e => handleFormFieldChange(cycleNo, 'product', e.target.value)} /></div>
                      <div><label className="block text-sm font-medium mb-1 text-gray-700">Party Name</label><input type="text" className="w-full border border-gray-300 rounded px-3 py-2 text-gray-900 bg-white" placeholder="Enter Party Name" value={formFields[cycleNo]?.partyName || ''} onChange={e => handleFormFieldChange(cycleNo, 'partyName', e.target.value)} /></div>
                      <div><label className="block text-sm font-medium mb-1 text-gray-700">Batch No</label><input type="text" className="w-full border border-gray-300 rounded px-3 py-2 text-gray-900 bg-white" placeholder="Enter Batch No" value={formFields[cycleNo]?.batchNo || ''} onChange={e => handleFormFieldChange(cycleNo, 'batchNo', e.target.value)} /></div>
                      <div><label className="block text-sm font-medium mb-1 text-gray-700">Executive Name</label><input type="text" className="w-full border border-gray-300 rounded px-3 py-2 text-gray-900 bg-white" placeholder="Enter Executive Name" value={formFields[cycleNo]?.executiveName || ''} onChange={e => handleFormFieldChange(cycleNo, 'executiveName', e.target.value)} /></div>
                      <div><label className="block text-sm font-medium mb-1 text-gray-700">Line No</label><input type="text" className="w-full border border-gray-300 rounded px-3 py-2 text-gray-900 bg-white" placeholder="Enter Line No" value={formFields[cycleNo]?.lineNo || ''} onChange={e => handleFormFieldChange(cycleNo, 'lineNo', e.target.value)} /></div>
                      <div><label className="block text-sm font-medium mb-1 text-gray-700">Packaged</label><input type="date" className="w-full border border-gray-300 rounded px-3 py-2 text-gray-900 bg-white" value={formFields[cycleNo]?.packaged || ''} onChange={e => handleFormFieldChange(cycleNo, 'packaged', e.target.value)} /></div>
                      <div><label className="block text-sm font-medium mb-1 text-gray-700">Expiry</label><input type="date" className="w-full border border-gray-300 rounded px-3 py-2 text-gray-900 bg-white" value={formFields[cycleNo]?.expiry || ''} onChange={e => handleFormFieldChange(cycleNo, 'expiry', e.target.value)} /></div>
                    </div>
                    <div className="flex justify-end">
                      <button onClick={() => handleStart(cycleNo)} className="px-4 py-2 bg-blue-600 text-white rounded">Start Session</button>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-end">
                    <button onClick={() => setProductFormExpandedByCycle(prev => ({ ...prev, [cycleNo]: true }))} className="px-4 py-2 bg-blue-600 text-white rounded">
                      {(status.submissions?.length || 0) >= 1 ? 'Add New Product' : 'Start Session'}
                    </button>
                  </div>
                )}
              </div>
            )}
            {status.started && !status.completed && Object.keys(selected[cycleNo] || {}).length > 0 && (
              <div className="space-y-6 mt-4">
                {items.map((item) => {
                  const current = selected[cycleNo]?.[item];
                  return (
                    <div key={item} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                      <div className="flex justify-between items-center">
                        <h3 className="font-semibold text-gray-900">{item}</h3>
                        <div className="flex gap-2">
                          <button onClick={() => handleSelect(cycleNo, item, "Not Okay")} className={`px-4 py-2 rounded-md text-sm font-medium ${current?.status === "Not Okay" ? "bg-red-600 text-white border border-red-600" : "bg-red-50 text-red-600 border border-red-200 hover:bg-red-100"}`}>Not Okay</button>
                          <button onClick={() => handleSelect(cycleNo, item, "Okay")} className={`px-4 py-2 rounded-md text-sm font-medium ${current?.status === "Okay" ? "bg-green-600 text-white border border-green-600" : "bg-green-50 text-green-600 border border-green-200 hover:bg-green-100"}`}>Okay</button>
                        </div>
                      </div>
                      {current?.status === "Not Okay" && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div><label className="block text-sm font-medium text-gray-700 mb-2">Select Category</label><select className="w-full px-3 py-2 border border-gray-300 rounded-md" value={current.category || ""} onChange={(e) => updateField(cycleNo, item, "category", e.target.value)}><option value="">Select</option><option value="Category A">Category A</option><option value="Category B">Category B</option><option value="Category C">Category C</option></select></div>
                            <div><label className="block text-sm font-medium text-gray-700 mb-2">Defect</label><input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Enter defect" value={current.defect || ""} onChange={(e) => updateField(cycleNo, item, "defect", e.target.value)} /></div>
                            <div className="sm:col-span-2"><label className="block text-sm font-medium text-gray-700 mb-2">Major Defect and Remarks</label><textarea className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Enter remarks" value={current.majorDefect || ""} onChange={(e) => updateField(cycleNo, item, "majorDefect", e.target.value)} rows={2} /></div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="flex justify-end gap-2 mt-4 flex-wrap">
                  <button className="px-4 py-2 border rounded">Cancel</button>
                  <button onClick={() => handleSave(cycleNo)} className="px-4 py-2 bg-blue-600 text-white rounded">Save Product Checklist</button>
                </div>
              </div>
            )}
            {status.completed && (
              <div className="border-t pt-4">
                <div className="flex items-center justify-between cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100" onClick={() => handleExpand(expandedCompletedCycle === cycleNo ? null : cycleNo)}>
                  <div className="flex items-center gap-2">
                    <span className="text-green-600 font-semibold">✓ Completed</span>
                    {(status.submissions?.length ?? 0) > 1 && <span className="text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full text-xs font-medium">{status.submissions?.length} product checklists</span>}
                    {status.defects.length > 0 && <span className="text-red-600 bg-red-100 px-2 py-0.5 rounded-full text-xs font-medium">{status.defects.length} Defect(s)</span>}
                    {status.okays.length > 0 && <span className="text-green-600 bg-green-100 px-2 py-0.5 rounded-full text-xs font-medium">{status.okays.length} Okay(s)</span>}
                    {totalMissedCount > 0 && <span className="text-yellow-600 bg-yellow-100 px-2 py-0.5 rounded-full text-xs font-medium">{totalMissedCount} Missed</span>}
                  </div>
                  <svg className={`w-5 h-5 transform ${expandedCompletedCycle === cycleNo ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                </div>
                {expandedCompletedCycle === cycleNo && (
                  <div className="pt-4 space-y-4">
                    {(status.submissions || []).length > 0 ? (
                      (status.submissions || []).map((sub, subIdx) => (
                        <div key={subIdx} className="p-4 bg-white border border-gray-200 rounded-lg space-y-3">
                          <div className="font-semibold text-gray-800 border-b pb-2">
                            Product {subIdx + 1}{sub.productName ? ` — ${sub.productName}` : ''}{sub.batchNo ? ` (Batch: ${sub.batchNo})` : ''}
                          </div>
                          <div className="space-y-3">
                            <div>
                              <div className="text-red-600 font-semibold mb-1 text-sm">Defects ({sub.defects.length})</div>
                              {sub.defects.length > 0 ? (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-left text-sm border rounded-md">
                                    <thead className="bg-red-50"><tr><th className="px-3 py-1.5 border font-medium">PP No</th><th className="px-3 py-1.5 border font-medium">Category</th><th className="px-3 py-1.5 border font-medium">Remarks</th></tr></thead>
                                    <tbody>
                                      {sub.defects.map((defect, i) => (
                                        <tr key={i}><td className="px-3 py-1.5 border">{sub.evaluationTypes?.[defect] || defect}</td><td className="px-3 py-1.5 border">{sub.defectCategories?.[defect] || '-'}</td><td className="px-3 py-1.5 border">{sub.defectRemarks?.[defect] || '-'}</td></tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : <div className="text-gray-500 text-sm py-1">None</div>}
                            </div>
                            <div>
                              <div className="text-green-700 font-semibold mb-1 text-sm">Okays ({sub.okays.length})</div>
                              {sub.okays.length > 0 ? (
                                <div className="bg-green-50 p-3 rounded"><span className="text-green-800 text-sm">{sub.okays.join(', ')}</span></div>
                              ) : <div className="text-gray-500 text-sm py-1">None</div>}
                            </div>
                            <div>
                              <div className="text-yellow-700 font-semibold mb-1 text-sm">Missed items ({Object.keys(sub.missedEvaluationTypes || {}).length})</div>
                              {Object.keys(sub.missedEvaluationTypes || {}).length > 0 ? (
                                <div className="bg-yellow-50 p-3 rounded">
                                  <div className="flex gap-2 flex-wrap">
                                    {Object.values(sub.missedEvaluationTypes || {}).map((item, i) => (
                                      <span key={i} className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded text-sm">{item}</span>
                                    ))}
                                  </div>
                                </div>
                              ) : <div className="text-gray-500 text-sm py-1">None</div>}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <>
                        {status.defects.length > 0 && (
                          <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm border rounded-md"><thead className="bg-red-50"><tr><th className="px-4 py-2 border font-medium">PP No</th><th className="px-4 py-2 border font-medium">Category</th><th className="px-4 py-2 border font-medium">Defect</th><th className="px-4 py-2 border font-medium">Remarks</th></tr></thead><tbody>
                            {status.defects.map((defect, idx) => (<tr key={idx}><td className="px-4 py-2 border">{status.evaluationTypes[defect] || defect}</td><td className="px-4 py-2 border">{status.defectCategories[defect] || 'B'}</td><td className="px-4 py-2 border">{selected[cycleNo]?.[defect]?.defect || defect}</td><td className="px-4 py-2 border">{selected[cycleNo]?.[defect]?.majorDefect || status.defectRemarks[defect] || '-'}</td></tr>))}
                            </tbody></table>
                          </div>
                        )}
                        {(status.okays.length > 0 || totalMissedCount > 0) && (
                          <div className="flex gap-4 flex-wrap">
                            {status.okays.length > 0 && <div className="bg-green-50 p-4 rounded-lg"><div className="text-green-700 font-semibold mb-2">Okays ({status.okays.length})</div><div className="flex gap-2 flex-wrap">{status.okays.map((o, i) => <span key={i} className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm">{status.okayEvaluationTypes[o] || o}</span>)}</div></div>}
                            {totalMissedCount > 0 && <div className="bg-yellow-50 p-4 rounded-lg"><div className="text-yellow-700 font-semibold mb-2">Missed ({totalMissedCount})</div><div className="flex gap-2 flex-wrap">{Object.values(status.missedEvaluationTypes || {}).map((m, i) => <span key={i} className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-sm">{m}</span>)}</div></div>}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ProductEvaluation;
